import type { CannedResponseSummary, MembershipSummary } from '@livechat/shared';
import { type FormEvent, useEffect, useState } from 'react';

import {
	createCannedResponse,
	deleteCannedResponse,
	listCannedResponses,
	updateCannedResponse,
} from '../api.js';

function ResponseEditor({
	canManageCompany,
	onChanged,
	response,
	tenantId,
}: {
	canManageCompany: boolean;
	onChanged: (response?: CannedResponseSummary) => void;
	response: CannedResponseSummary;
	tenantId: string;
}) {
	const [editing, setEditing] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string>();
	const canManage = response.scope === 'personal' || canManageCompany;

	async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		setBusy(true);
		setError(undefined);
		const data = new FormData(event.currentTarget);
		try {
			onChanged(
				await updateCannedResponse(tenantId, response.id, {
					body: String(data.get('body')),
					name: String(data.get('name')),
					trigger: String(data.get('trigger')).trim() || null,
				}),
			);
			setEditing(false);
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not update the response');
		} finally {
			setBusy(false);
		}
	}

	async function remove(): Promise<void> {
		if (!window.confirm(`Delete “${response.name}”? This cannot be undone.`)) return;
		setBusy(true);
		setError(undefined);
		try {
			await deleteCannedResponse(tenantId, response.id);
			onChanged(undefined);
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not delete the response');
		} finally {
			setBusy(false);
		}
	}

	async function enable(): Promise<void> {
		setBusy(true);
		setError(undefined);
		try {
			onChanged(
				await updateCannedResponse(tenantId, response.id, {
					status: 'active',
				}),
			);
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not change the response');
		} finally {
			setBusy(false);
		}
	}

	return (
		<article className={`canned-response-card ${response.status}`}>
			<header>
				<div>
					<span className={`scope-badge ${response.scope}`}>{response.scope}</span>
					{response.status === 'disabled' && (
						<span className="status-pill disabled">disabled</span>
					)}
					<div className="canned-response-title">{response.name}</div>
					{response.trigger && <code>/{response.trigger}</code>}
				</div>
				{canManage && (
					<div className="card-actions">
						<button
							className="text-button"
							onClick={() => setEditing((value) => !value)}
							type="button"
						>
							{editing ? 'Cancel' : 'Edit'}
						</button>
						{response.status === 'disabled' && (
							<button
								className="text-button"
								disabled={busy}
								onClick={() => void enable()}
								type="button"
							>
								Enable
							</button>
						)}
						<button
							className="text-button danger"
							disabled={busy}
							onClick={() => void remove()}
							type="button"
						>
							Delete
						</button>
					</div>
				)}
			</header>
			{error && <div className="alert error">{error}</div>}
			{editing ? (
				<form
					className="canned-response-form compact"
					onSubmit={(event) => void save(event)}
				>
					<label>
						<span>Name</span>
						<input defaultValue={response.name} maxLength={160} name="name" required />
					</label>
					<label>
						<span>Short trigger</span>
						<input
							defaultValue={response.trigger}
							maxLength={80}
							name="trigger"
							pattern="[A-Za-z0-9][A-Za-z0-9_-]*"
							placeholder="shipping"
						/>
					</label>
					<label className="form-span">
						<span>Response</span>
						<textarea
							defaultValue={response.body}
							maxLength={10_000}
							name="body"
							required
							rows={5}
						/>
					</label>
					<button className="primary-button" disabled={busy}>
						{busy ? 'Saving…' : 'Save response'}
					</button>
				</form>
			) : (
				<p className="canned-response-body">{response.body}</p>
			)}
		</article>
	);
}

export function TenantCannedResponses({ membership }: { membership: MembershipSummary }) {
	const [responses, setResponses] = useState<CannedResponseSummary[]>([]);
	const [showCreate, setShowCreate] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string>();
	const canManageCompany = membership.role === 'owner' || membership.role === 'admin';

	useEffect(() => {
		setError(undefined);
		void listCannedResponses(membership.tenantId)
			.then(setResponses)
			.catch((caught: unknown) =>
				setError(
					caught instanceof Error ? caught.message : 'Could not load canned responses',
				),
			);
	}, [membership.tenantId]);

	async function create(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		setBusy(true);
		setError(undefined);
		const form = event.currentTarget;
		const data = new FormData(form);
		try {
			const created = await createCannedResponse(membership.tenantId, {
				body: String(data.get('body')),
				name: String(data.get('name')),
				scope: String(data.get('scope')) as CannedResponseSummary['scope'],
				trigger: String(data.get('trigger')).trim() || undefined,
			});
			setResponses((current) => [...current, created]);
			setShowCreate(false);
			form.reset();
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not create the response');
		} finally {
			setBusy(false);
		}
	}

	return (
		<div>
			<div className="subsection-heading">
				<div>
					<h3>Canned responses</h3>
					<p>Save reusable company replies or a private library for this workspace.</p>
				</div>
				<button className="primary-button" onClick={() => setShowCreate((value) => !value)}>
					{showCreate ? 'Cancel' : 'New response'}
				</button>
			</div>
			{error && <div className="alert error">{error}</div>}
			{showCreate && (
				<form
					className="canned-response-form create-configuration"
					onSubmit={(event) => void create(event)}
				>
					<label>
						<span>Name</span>
						<input maxLength={160} name="name" placeholder="Delivery update" required />
					</label>
					<label>
						<span>Short trigger</span>
						<input
							maxLength={80}
							name="trigger"
							pattern="[A-Za-z0-9][A-Za-z0-9_-]*"
							placeholder="shipping"
						/>
					</label>
					<label>
						<span>Owner</span>
						<select defaultValue="personal" name="scope">
							<option value="personal">Personal · only me</option>
							{canManageCompany && (
								<option value="company">Company · all agents</option>
							)}
						</select>
					</label>
					<label className="form-span">
						<span>Response</span>
						<textarea
							maxLength={10_000}
							name="body"
							placeholder="Your delivery is due…"
							required
							rows={6}
						/>
					</label>
					<button className="primary-button" disabled={busy}>
						{busy ? 'Creating…' : 'Create response'}
					</button>
				</form>
			)}
			<div className="canned-response-grid">
				{responses.map((response) => (
					<ResponseEditor
						canManageCompany={canManageCompany}
						key={response.id}
						onChanged={(updated) =>
							setResponses((current) =>
								updated
									? current.map((item) =>
											item.id === updated.id ? updated : item,
										)
									: current.filter((item) => item.id !== response.id),
							)
						}
						response={response}
						tenantId={membership.tenantId}
					/>
				))}
				{!responses.length && (
					<div className="empty-configuration">No canned responses yet.</div>
				)}
			</div>
		</div>
	);
}
